import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export type AuditEntity = "subscription" | "recipient_label" | "suppression";

export type AuditRow = {
  id: string;
  entity: string;
  action: string;
  email: string | null;
  summary: string;
  record_id: string | null;
  created_at: string;
};

/**
 * Admin-only read of the automatic change history (subscriptions, recipient
 * labels, suppressions). Written exclusively by database triggers.
 */
export const getAuditLog = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { entity?: string; email?: string; limit?: number } | undefined) => input ?? {})
  .handler(async ({ data, context }): Promise<{ rows: AuditRow[] }> => {
    const { assertAdmin } = await import("./admin.server");
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const limit = Math.min(Math.max(data.limit ?? 200, 1), 500);
    let query = supabaseAdmin
      .from("audit_log")
      .select("id, entity, action, email, summary, record_id, created_at")
      .order("created_at", { ascending: false })
      .limit(limit);

    if (data.entity && data.entity !== "all") query = query.eq("entity", data.entity);
    const email = data.email?.trim().toLowerCase();
    if (email) query = query.ilike("email", `%${email}%`);

    const { data: rows, error } = await query;
    if (error) throw new Error(error.message);
    return { rows: (rows ?? []) as AuditRow[] };
  });
